import colorLib from "@kurkle/color";
import moment from "jalali-moment";

export const guid = () => {
  var S4 = function () {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return (
    S4() +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    S4() +
    S4()
  );
};

export const randomArrayShuffle = (array) => {
  var currentIndex = array.length,
    temporaryValue,
    randomIndex;
  while (0 !== currentIndex) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }
  return array;
};

export const toJalaliDate = (date) => {
  const parts = parseApiLocalDateTimeParts(date);
  if (!parts) return "";
  return moment(
    `${parts.year}-${parts.month}-${parts.day}`,
    "YYYY-MM-DD"
  ).format("jYYYY-jMM-jDD");
};

/**
 * Appointment / business DateTimes are stored as Iran wall-clock (no real TZ).
 * Ignore trailing Z/offset so 12:45 is never shown as 09:15 via UTC conversion.
 */
export const parseApiLocalDateTimeParts = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime()) || value.getFullYear() <= 1) return null;
    return {
      year: value.getFullYear(),
      month: String(value.getMonth() + 1).padStart(2, "0"),
      day: String(value.getDate()).padStart(2, "0"),
      hour: String(value.getHours()).padStart(2, "0"),
      minute: String(value.getMinutes()).padStart(2, "0"),
      second: String(value.getSeconds()).padStart(2, "0"),
    };
  }

  const trimmed = String(value).trim();
  if (!trimmed || trimmed.startsWith("0001-01-01")) return null;

  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?/
  );
  if (!match) {
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1) return null;
    return parseApiLocalDateTimeParts(d);
  }

  return {
    year: Number(match[1]),
    month: match[2],
    day: match[3],
    hour: match[4] || "00",
    minute: match[5] || "00",
    second: match[6] || "00",
  };
};

export const parseApiLocalDateTime = (value) => {
  const parts = parseApiLocalDateTimeParts(value);
  if (!parts) return null;
  return new Date(
    parts.year,
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
};
 

export const getYesterday = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1); // Subtract one day to get yesterday
  return date ? moment(date).format("YYYY-MM-DD") + " 00:00:00" : null;
};


export const toMiladyDate = (date) => {
  return date && date !== "0001-01-01T00:00:00"
    ? moment(date).format("YYYY-MM-DD") + " 00:00:00"
    : null;
};

/**
 * Calendar date for API without UTC shift.
 * Never use Date#toISOString() for date-only fields (Iran UTC+3:30 loses a day).
 */
export const formatLocalDateForApi = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const hasUtcMarker = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    if (match && !hasUtcMarker) {
      return `${match[1]}-${match[2]}-${match[3]}T00:00:00`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}T00:00:00`;
};

/**
 * Local wall-clock DateTime for API without UTC shift.
 * Never send Date#toISOString() for appointment times (Iran UTC+3:30 turns 12:45 into 09:15).
 */
export const formatLocalDateTimeForApi = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
    );
    const hasUtcMarker = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    if (match && !hasUtcMarker) {
      const sec = match[6] || "00";
      return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${sec}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${day}T${hh}:${mm}:${ss}`;
};

/** YYYY-MM-DD from local calendar parts (not UTC). */
export const toLocalDateInputValue = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const hasUtcMarker = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    if (match && !hasUtcMarker) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const toMiladyFullDate = (date) => {
  return date && date !== "0001-01-01T00:00:00"
    ? moment(date).format("YYYY-MM-DD HH:mm:ss")
    : null;
};

export const toJalaliDayDate = (date) => {
  return date ? moment(date).format("jD") : "";
};

export const toJalaliFullDate = (date) => {
  const parts = parseApiLocalDateTimeParts(date);
  if (!parts) return "";
  return moment(
    `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`,
    "YYYY-MM-DD HH:mm:ss"
  ).format("jYYYY-jMM-jDD HH:mm");
};

export const toJalaliMonthName = (date) => {
  return date
    ? new Intl.DateTimeFormat("fa-IR", { month: "short" }).format(date)
    : "";
};

export const currencyFormat = (num) => {
  if (isNaN(num)) {
    return num;
  } else {
    return "" + num.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  }
};

export const numberParser = (value) =>
  parseFloat(value.replace(/\$\s?|(,*)/g, ""));

export const numberFormatter = (value) => {
  return value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const getLastDateOfMonthInfo = (date) => {
  const currentPersianDate = moment(date).locale("fa").add(1, "month");
  const firstDayOfNextMonthSt =
    currentPersianDate.format("jYYYY") +
    "/" +
    currentPersianDate.format("jMM") +
    "/01";
  //and then
  const firstDayOfNextMonth = moment.from(
    firstDayOfNextMonthSt,
    "fa",
    "YYYY/MM/DD"
  );
  const lastDayOfMonth = firstDayOfNextMonth.add(-1, "d");

  const firstDayOfMonthJalali =
    lastDayOfMonth.format("jYYYY") + "/" + lastDayOfMonth.format("jMM") + "/01";

  const firstDayOfMonth = moment.from(
    firstDayOfMonthJalali,
    "fa",
    "YYYY/MM/DD"
  );

  // console.log("firstDayOfMonth", firstDayOfMonth.format("jYYYY-jMM-jDD"));
  // console.log("lastDayOfMonth", lastDayOfMonth.format("jYYYY-jMM-jDD"));

  return [
    new Date(firstDayOfMonth.format("YYYY-MM-DD")),
    new Date(lastDayOfMonth.format("YYYY-MM-DD")),
  ];
};

export const onFindSpecificNode = (pathnames, menuList) => {
  let specificNode = menuList;

  pathnames.forEach((path) => {
    const item = specificNode.child.find((menu) => menu.oldLink === path);

    if (item) {
      specificNode = item;
    }
  });

  return specificNode;
};

export const filterArrayByArrayId = ({ list, key, ids = [] }) => {
  let finalList = [];

  if (ids.length > 0) {
    finalList = list.filter((option) => {
      return ids.findIndex((x) => x === option[key]) >= 0;
    });
  }

  return finalList;
};

export const onCheckIsShowMainMenu = () => {
  let user = localStorage.getItem("user");

  if (user) {
    user = JSON.parse(user);

    // Contractor portal users always need chrome; members previously had this forced false.
    const roles = Array.isArray(user.roleNames)
      ? user.roleNames
      : Array.isArray(user.RoleNames)
        ? user.RoleNames
        : [];
    const hasContractorRole = roles.some(
      (r) =>
        String(r).toLowerCase() === "contractor" ||
        String(r).toLowerCase() === "contractormember"
    );
    if (hasContractorRole) {
      return true;
    }

    return user.isShowMainMenu;
  }

  return true;
};

export function transparentize(value, opacity) {
  var alpha = opacity === undefined ? 0.5 : opacity;
  return colorLib(value).alpha(alpha).rgbString();
}
